import React from 'react';
import { ShieldCheck, Clock, Languages, FileCheck } from 'lucide-react';
import { useLanguage, Language } from '../contexts/LanguageContext';
type Card = {
  title: string;
  description: string;
};
type BenefitStrings = {
  heading: string;
  subtitle: string;
  cards: Card[];
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, BenefitStrings> = {
  en: {
    heading: 'A better way to prepare your forms',
    subtitle: "We've simplified the paperwork so you can get your passport without struggling through forms in a language that isn't yours.",
    cards: [{
      title: 'Save Time & Hassle',
      description: 'Answer simple questions in your language. We put your answers onto the correct U.S. forms for you — no decoding required.'
    }, {
      title: 'Fewer Mistakes',
      description: 'Our system checks for common omissions and formatting errors, and a specialist reviews your form, reducing the chance of delays or rejection.'
    }, {
      title: 'In Your Language',
      description: 'Go through the whole process in English, Spanish, Haitian Creole, or Vietnamese. We handle the official English form for you.'
    }, {
      title: 'Ready to Submit',
      description: 'Receive a completed, print-ready form with clear instructions on where to sign, what to bring, and where to take it.'
    }]
  },
  es: {
    heading: 'Una mejor manera de preparar sus formularios',
    subtitle: 'Simplificamos el papeleo para que obtenga su pasaporte sin batallar con formularios en un idioma que no es el suyo.',
    cards: [{
      title: 'Ahorre tiempo y molestias',
      description: 'Responda preguntas sencillas en su idioma. Nosotros pasamos sus respuestas a los formularios correctos de EE. UU. — sin que tenga que descifrar nada.'
    }, {
      title: 'Menos errores',
      description: 'Nuestro sistema revisa omisiones y errores de formato comunes, y un especialista revisa su formulario, reduciendo el riesgo de demoras o rechazo.'
    }, {
      title: 'En su idioma',
      description: 'Haga todo el proceso en inglés, español, creole haitiano o vietnamita. Nosotros nos encargamos del formulario oficial en inglés.'
    }, {
      title: 'Listo para entregar',
      description: 'Reciba un formulario completo y listo para imprimir, con instrucciones claras de dónde firmar, qué llevar y a dónde ir.'
    }]
  },
  ht: {
    heading: 'Yon pi bon fason pou prepare fòm ou yo',
    subtitle: 'Nou senplifye papye yo pou ou ka jwenn paspò ou san ou pa goumen ak fòm nan yon lang ki pa pa w.',
    cards: [{
      title: 'Ekonomize tan ak traka',
      description: 'Reponn kesyon senp nan lang ou. Nou mete repons ou yo sou bon fòm ameriken yo pou ou — ou pa bezwen dechifre anyen.'
    }, {
      title: 'Mwens erè',
      description: 'Sistèm nou an tcheke omisyon ak erè fòma komen, epi yon espesyalis tcheke fòm ou an, sa redui chans reta oswa rejè.'
    }, {
      title: 'Nan lang ou',
      description: 'Fè tout pwosesis la an anglè, panyòl, kreyòl ayisyen, oswa vyetnamyen. Nou okipe fòm ofisyèl an anglè a pou ou.'
    }, {
      title: 'Pare pou soumèt',
      description: 'Resevwa yon fòm konplè, pare pou enprime, ak enstriksyon klè sou ki kote pou siyen, kisa pou pote, ak ki kote pou ale.'
    }]
  },
  vi: {
    heading: 'Cách tốt hơn để chuẩn bị mẫu đơn của quý vị',
    subtitle: 'Chúng tôi đã đơn giản hóa thủ tục giấy tờ để quý vị có thể nhận hộ chiếu mà không phải vật lộn với mẫu đơn bằng ngôn ngữ không phải của mình.',
    cards: [{
      title: 'Tiết kiệm thời gian và phiền phức',
      description: 'Trả lời các câu hỏi đơn giản bằng ngôn ngữ của quý vị. Chúng tôi điền câu trả lời của quý vị vào đúng mẫu đơn Hoa Kỳ — không cần phải giải mã gì cả.'
    }, {
      title: 'Ít sai sót hơn',
      description: 'Hệ thống của chúng tôi kiểm tra các thiếu sót và lỗi định dạng thường gặp, và một chuyên viên xem lại mẫu đơn của quý vị, giảm nguy cơ chậm trễ hoặc bị từ chối.'
    }, {
      title: 'Bằng ngôn ngữ của quý vị',
      description: 'Thực hiện toàn bộ quy trình bằng tiếng Anh, Tây Ban Nha, Creole Haiti hoặc tiếng Việt. Chúng tôi lo mẫu đơn chính thức bằng tiếng Anh cho quý vị.'
    }, {
      title: 'Sẵn sàng để nộp',
      description: 'Nhận mẫu đơn đã hoàn tất, sẵn sàng để in, kèm hướng dẫn rõ ràng về nơi ký, những gì cần mang theo, và nơi nộp.'
    }]
  }
};
const ICONS = [<Clock className="w-6 h-6 text-brand-accent" data-id="element-48" data-mp="3ndasu2yyj7vc" />, <ShieldCheck className="w-6 h-6 text-brand-accent" data-id="element-49" data-mp="3nayd7wgo99lg" />, <Languages className="w-6 h-6 text-brand-accent" data-id="element-50" data-mp="3if0xnsd8g79q" />, <FileCheck className="w-6 h-6 text-brand-accent" data-id="element-51" data-mp="3ic50i697lmmc" />];
export function BenefitCards() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  return <section className="py-24 bg-white relative z-10" data-id="element-52" data-mp="3uv0mc6ppyxj5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-53" data-mp="3i60g5xx9jxm">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-54" data-mp="3jz6bj0po04k">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-55" data-mp="8fxq0tb3gtyn">
            {t.heading}
          </h2>
          <p className="text-lg text-brand-textSecondary" data-id="element-56" data-mp="37xxck3v4vnvp">{t.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-57" data-mp="3by7so9cn3uoa">
          {t.cards.map((benefit, index) => <div key={index} className="bg-brand-bg/30 rounded-2xl p-8 border border-brand-border hover:shadow-premium transition-shadow duration-300 group" data-id="element-58" data-mp="3gvhezg4qfq7w" data-mpi={index}>
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm border border-brand-border mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-59" data-mp="w6bgwghy0mrn">
                {ICONS[index]}
              </div>
              <h3 className="text-xl font-bold text-brand-textPrimary mb-3" data-id="element-60" data-mp="6ixoe5fc5vsj">
                {benefit.title}
              </h3>
              <p className="text-brand-textSecondary leading-relaxed" data-id="element-61" data-mp="ledoar2lklwu">
                {benefit.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}